import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { FaFacebookF, FaInstagram, FaYoutube } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { Menu, X, ExternalLink, ArrowRight, Play, ChevronLeft, ChevronRight, Newspaper } from "lucide-react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ========== FADE-IN WRAPPER ========== */
const FadeIn = ({ children, delay = 0, direction = "up", className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const variants = {
    hidden: {
      opacity: 0,
      y: direction === "up" ? 40 : direction === "down" ? -40 : 0,
      x: direction === "left" ? 40 : direction === "right" ? -40 : 0,
    },
    visible: {
      opacity: 1, y: 0, x: 0,
      transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <motion.div ref={ref} variants={variants} initial="hidden" animate={isInView ? "visible" : "hidden"} className={className}>
      {children}
    </motion.div>
  );
};

/* ========== NAVIGATION ========== */
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const links = [
    { label: "Libro", id: "book" },
    { label: "Biografia", id: "bio" },
    { label: "TV", id: "tv-show" },
    { label: "News", id: "news" },
    { label: "Contatti", id: "contact" },
  ];

  return (
    <>
      <nav className={`nav-container ${scrolled ? "scrolled" : ""}`} data-testid="main-navigation">
        <div className="nav-inner">
          <a href="#hero" className="nav-logo" onClick={(e) => handleNavClick(e, "hero")} data-testid="nav-logo">
            EVA CROSETTA
          </a>
          <ul className="nav-links">
            {links.map((link) => (
              <li key={link.id}>
                <a href={`#${link.id}`} onClick={(e) => handleNavClick(e, link.id)} data-testid={`nav-link-${link.id}`}>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <button className="mobile-menu-btn" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label="Apri menu">
            <Menu size={24} />
          </button>
        </div>
      </nav>
      <div className={`mobile-nav ${mobileOpen ? "open" : ""}`} data-testid="mobile-navigation">
        <button className="mobile-close-btn" onClick={() => setMobileOpen(false)} data-testid="mobile-close-btn" aria-label="Chiudi menu">
          <X size={28} />
        </button>
        {links.map((link) => (
          <a key={link.id} href={`#${link.id}`} onClick={(e) => handleNavClick(e, link.id)} data-testid={`mobile-nav-link-${link.id}`}>
            {link.label}
          </a>
        ))}
      </div>
    </>
  );
};

/* ========== HERO CAROUSEL ========== */
const heroImages = [
  "/images/hero-1-D1A2149.webp",
  "/images/hero-2-D1A1939.webp",
  "/images/hero-3-D1A1880.webp",
  "/images/hero-4-D1A1778.webp",
  "/images/hero-5-D1A1735.webp",
  "/images/hero-6-D1A1850.webp",
];

const HeroSection = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setDirection(1);
      setCurrent((prev) => (prev + 1) % heroImages.length);
    }, 5000);
  }, []);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const goTo = (idx) => {
    setDirection(idx > current ? 1 : -1);
    setCurrent(idx);
    startTimer();
  };

  const goPrev = () => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + heroImages.length) % heroImages.length);
    startTimer();
  };

  const goNext = () => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % heroImages.length);
    startTimer();
  };

  const slideVariants = {
    enter: (dir) => ({ opacity: 0, scale: 1.08, x: dir > 0 ? 60 : -60 }),
    center: { opacity: 1, scale: 1, x: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
    exit: (dir) => ({ opacity: 0, scale: 0.98, x: dir > 0 ? -60 : 60, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }),
  };

  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      <AnimatePresence custom={direction} mode="popLayout">
        <motion.div
          key={current}
          className="hero-image-container"
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
        >
          <img src={heroImages[current]} alt={`Eva Crosetta - Foto ${current + 1}`} loading={current === 0 ? "eager" : "lazy"} />
        </motion.div>
      </AnimatePresence>
      <div className="hero-overlay" />
      <div className="hero-content">
        <motion.p className="hero-overline"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          Conduttrice TV &middot; Autrice
        </motion.p>
        <motion.h1 className="hero-title"
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          Eva<br />Crosetta
        </motion.h1>
      </div>
      <button className="hero-arrow hero-arrow-left" onClick={goPrev} data-testid="hero-carousel-prev" aria-label="Precedente">
        <ChevronLeft size={28} />
      </button>
      <button className="hero-arrow hero-arrow-right" onClick={goNext} data-testid="hero-carousel-next" aria-label="Successivo">
        <ChevronRight size={28} />
      </button>
      <div className="hero-dots" data-testid="hero-carousel-dots">
        {heroImages.map((_, i) => (
          <button key={i} className={`hero-dot ${i === current ? "active" : ""}`}
            onClick={() => goTo(i)} data-testid={`hero-dot-${i}`} aria-label={`Vai alla foto ${i + 1}`} />
        ))}
      </div>
      <div className="hero-scroll-indicator">
        <span>Scroll</span>
        <div className="scroll-line" />
      </div>
    </section>
  );
};

/* ========== BOOK ========== */
const BookSection = () => {
  const buyLinks = [
    { name: "Amazon", url: "https://www.amazon.it/colpa-sono-Congo-allombra-mango/dp/8817188743/" },
    { name: "Mondadori", url: "https://www.mondadoristore.it/Che-colpa-ne-ho-se-sono-nato-in-Congo-all-ombra-di-un-mango-Un-viaggio-tra-le-storie-delle-persone-che-ho-incontrato-in-Africa-Eva-Crosetta/eai978881718874/" },
    { name: "IBS", url: "https://www.ibs.it/che-colpa-ne-ho-se-libro-eva-crosetta/e/9788817188746" },
    { name: "Feltrinelli", url: "https://www.lafeltrinelli.it/che-colpa-ne-ho-se-libro-eva-crosetta/e/9788817188746" },
  ];

  return (
    <section id="book" className="book-section" data-testid="book-section">
      <div className="book-inner">
        <FadeIn><p className="book-overline">Nuovo Libro</p></FadeIn>
        <div className="book-grid">
          <FadeIn delay={0.2} direction="left">
            <div className="book-cover-wrapper">
              <img src="/images/book-cover.webp"
                alt="Che colpa ne ho se sono nato in Congo all'ombra di un mango?" data-testid="book-cover-image" />
            </div>
          </FadeIn>
          <FadeIn delay={0.3} direction="right">
            <div>
              <h2 className="book-title">Che colpa ne ho se sono nato in Congo all&apos;ombra di un mango?</h2>
              <p className="book-publisher">Rizzoli</p>
              <p className="book-description">
                A volte basta poco perch&eacute; la nostra vita prenda una svolta inaspettata e ci porti
                in un posto bellissimo. &Egrave; quello che racconta in questo libro Eva Crosetta,
                conduttrice e volto noto della televisione italiana. In questo diario di viaggio
                pieno di delicata ironia, di riconoscenza e di grazia, Eva ci accompagna a scoprire
                il suo percorso di conversione e fede e a vivere con lei le esperienze nella missione
                di Kinshasa: tra scene quotidiane a volte commoventi, a volte dure e al limite della
                crudelt&agrave;. Un libro sincero e ricco di umanit&agrave;, per sentire da chi l&apos;ha
                provato davvero il potere trasformativo della misericordia di Dio.
              </p>
              <div className="book-links">
                {buyLinks.map((link) => (
                  <a key={link.name} href={link.url} target="_blank" rel="noopener noreferrer"
                    className="book-link" data-testid={`buy-link-${link.name.toLowerCase()}`}>
                    {link.name} <ExternalLink size={14} />
                  </a>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

/* ========== BIOGRAPHY ========== */
const BiographySection = () => (
  <section id="bio" className="bio-section" data-testid="bio-section">
    <div className="bio-inner">
      <FadeIn direction="left">
        <div className="bio-image-wrapper">
          <img
            src="/images/bio-D1A2228.webp"
            alt="Eva Crosetta - Biografia"
            data-testid="bio-image"
          />
        </div>
      </FadeIn>
      <FadeIn direction="right" delay={0.2}>
        <div>
          <p className="bio-overline">Biografia</p>
          <h2 className="bio-title">Un po&apos; di me</h2>
          <div className="bio-text">
            <span className="drop-cap">S</span>
            <p>
              ono nata a Castelfranco Veneto (TV) il 29 agosto 1976. Pesavo 3,4 Kg.,
              centimetri di lunghezza non pervenuti ma sono diventati molti di pi&ugrave;!
            </p>
            <br />
            <p>
              Un giorno mio padre acquist&ograve; una delle prime videocamere per i classici
              filmini di famiglia, decisione della quale con il tempo si pent&igrave; perch&eacute;
              da quel momento fu costretto a riprendere le mie trasmissioni TV farcite di balletti
              improvvisati sulla musica di Luis Miguel e con ospiti in studio rappresentati da
              bambolotti e da mia sorella Elena che ancora non si reggeva in piedi.
            </p>
            <br />
            <p>
              A 12 anni girai il mio primo ed unico film Horror nel giardino di casa, rubando a
              mio padre un impermeabile ed un passamontagna da sci e consumando molto ketchup!
            </p>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>
);

/* ========== TV SHOW - REDESIGNED ========== */
const tvBgImages = [
  "/images/tv-bg-D1A1795.webp",
  "/images/tv-bg-D1A1880.webp",
  "/images/tv-bg-D1A1760.webp",
];

const TVShowSection = () => {
  const [bgIndex, setBgIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % tvBgImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section id="tv-show" className="tv-section-new" data-testid="tv-section">
      {/* Auto-scrolling background */}
      <div className="tv-bg-slideshow">
        <AnimatePresence mode="popLayout">
          <motion.img
            key={bgIndex}
            src={tvBgImages[bgIndex]}
            alt="Sulla Via di Damasco"
            className="tv-bg-image"
            initial={{ opacity: 0, scale: 1 }}
            animate={{ opacity: 0.65, scale: 1.08, transition: { duration: 6, ease: "linear", opacity: { duration: 1.2 } } }}
            exit={{ opacity: 0, transition: { duration: 1, ease: "easeInOut" } }}
          />
        </AnimatePresence>
      </div>
      <div className="tv-bg-overlay" />

      {/* Foreground content */}
      <div className="tv-fg-content">
        <FadeIn>
          <p className="tv-overline">Programma TV</p>
        </FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="tv-title-new">Sulla Via<br />di Damasco</h2>
        </FadeIn>
        <FadeIn delay={0.2}>
          <div className="tv-badge-new">
            <img src="/images/rai3-logo.jpg" alt="Rai 3" className="tv-badge-logo" />
            <span>Domenica Mattina &middot; Ore 08:30</span>
          </div>
        </FadeIn>
        <FadeIn delay={0.3}>
          <p className="tv-description-new">
            Conduco il programma da 4 anni, raccontando approfondimenti sulla variegata
            realt&agrave; ecumenica, umana e sociale, dedita al volontariato sul territorio
            non solo italiano. La testimonianza che i valori positivi di solidariet&agrave;
            della tradizione cristiana possono essere vissuti nel concreto come fonte di speranza,
            per un mondo presente e futuro meno lacerato.
          </p>
        </FadeIn>
        <FadeIn delay={0.4}>
          <a
            href="https://www.raiplay.it/video/2022/06/Sulla-via-di-Damasco-0e5a5220-5162-4a92-b83a-8a389958086a.html"
            target="_blank" rel="noopener noreferrer"
            className="tv-cta-new" data-testid="tv-raiplay-link"
          >
            <Play size={16} />
            Rivedi le Puntate su RaiPlay
            <ArrowRight size={16} />
          </a>
        </FadeIn>
      </div>
    </section>
  );
};

/* ========== POPUP FESTIVAL ========== */
const FestivalPopup = () => {
  const [visible, setVisible] = useState(true);

  if (!visible || new Date() > new Date("2026-10-04T23:59:59")) return null;

  return (
    <div className="premio-popup" data-testid="festival-popup">
      <button className="premio-popup-close" onClick={() => setVisible(false)} aria-label="Chiudi" data-testid="festival-popup-close">
        <X size={16} />
      </button>
      <a href="/news-tra-la-piazza-e-il-cielo-campania-libri-festival" className="premio-popup-link" data-testid="festival-popup-image-link">
        <img src="/images/campania-libri-festival.webp" alt="Campania Libri Festival 2026 - Palazzo Reale e Biblioteca Nazionale, Napoli 1-4 ottobre" />
      </a>
      <a href="/news-tra-la-piazza-e-il-cielo-campania-libri-festival" className="popup-news-btn" data-testid="festival-popup-leggi-btn">
        Leggi News <ArrowRight size={15} />
      </a>
    </div>
  );
};

/* ========== COMUNICATI STAMPA ========== */
const comunicatiList = [
  {
    id: "custodireamore2",
    date: "5 Novembre 2026",
    puntata: "Speciale \u00b7 TX 06.11.2026, ore 15.30",
    title: "Sulla Via di Damasco, RAI Tre: \u201cCustodire l\u2019amore\u201d",
    link: "/comunicato-stampa-sulla-via-di-damasco-custodire-l-amore-6-novembre",
  },
  {
    id: "santogiovani",
    date: "30 Ottobre 2026",
    puntata: "Puntata n. 44 \u00b7 TX 01.11.2026",
    title: "Sulla Via di Damasco, RAI Tre: \u201cIl santo dei giovani\u201d",
    link: "/comunicato-stampa-sulla-via-di-damasco-il-santo-dei-giovani",
  },
  {
    id: "custodireamore",
    date: "23 Ottobre 2026",
    puntata: "Puntata n. 43 \u00b7 TX 25.10.2026",
    title: "Sulla Via di Damasco, RAI Tre: \u201cCustodire l\u2019amore\u201d",
    link: "/comunicato-stampa-sulla-via-di-damasco-custodire-l-amore",
  },
  {
    id: "maipiusoli",
    date: "9 Ottobre 2026",
    puntata: "Puntata n. 41 \u00b7 TX 11.10.2026",
    title: "Sulla Via di Damasco, RAI Tre: \u201cMai pi\u00f9 soli\u201d",
    link: "/comunicato-stampa-sulla-via-di-damasco-mai-piu-soli",
  },
  {
    id: "santogioia",
    date: "2 Ottobre 2026",
    puntata: "Puntata n. 40 \u00b7 TX 04.10.2026",
    title: "Sulla Via di Damasco, RAI Tre: \u201cFrancesco, il santo della gioia\u201d",
    link: "/comunicato-stampa-sulla-via-di-damasco-francesco-il-santo-della-gioia",
  },
  {
    id: "senzapaura",
    date: "25 Settembre 2026",
    puntata: "Puntata n. 39 \u00b7 TX 27.09.2026",
    title: "Sulla Via di Damasco, RAI Tre: \u201cSenza paura\u201d",
    link: "/comunicato-stampa-sulla-via-di-damasco-senza-paura",
  },
  {
    id: "maria",
    date: "16 Settembre 2026",
    puntata: "Puntata n. 38 \u00b7 TX 20.09.2026",
    title: "Sulla Via di Damasco, RAI Tre: \u201cMaria parla oggi\u201d",
    link: "/comunicato-stampa-sulla-via-di-damasco-maria-parla-oggi",
  },
  {
    id: "gratitudine",
    date: "7 Agosto 2026",
    puntata: "Puntata n. 32 \u00b7 TX 09.08.2026",
    title: "Sulla Via di Damasco, RAI Tre: \u201cLa forza della gratitudine\u201d",
    link: "/comunicato-stampa-sulla-via-di-damasco-la-forza-della-gratitudine",
  },
  {
    id: "francesco",
    date: "31 Luglio 2026",
    puntata: "Puntata n. 31 \u00b7 TX 02.08.2026",
    title: "Sulla Via di Damasco, RAI Tre: \u201cNel nome di Francesco\u201d",
    link: "/comunicato-stampa-sulla-via-di-damasco-nel-nome-di-francesco",
  },
  {
    id: "segreti",
    date: "27 Luglio 2026",
    puntata: "Segreti d'Autore 2026 \u00b7 29 Luglio 2026",
    title: "Premio Segreti d'Autore 2026 a Eva Crosetta",
    link: "/comunicato-segreti-autore-premio-eva-crosetta-29-luglio-2026",
  },
];

const ComunicatiStampaSection = () => (
  <section className="comunicati-section" data-testid="comunicati-section">
    <div className="comunicati-inner">
      <FadeIn>
        <p className="comunicati-overline">Comunicati Stampa RAI</p>
      </FadeIn>
      {comunicatiList.slice(0, 2).map((c, i) => (
        <FadeIn key={c.id} delay={0.15 + i * 0.1}>
          <a href={c.link} className="comunicato-link" data-testid={`comunicato-${c.id}-btn`}>
            <Newspaper size={18} />
            <span>Comunicato Stampa {c.title}</span>
            <ArrowRight size={16} />
          </a>
        </FadeIn>
      ))}
      <FadeIn delay={0.35}>
        <a href="/comunicati-stampa" className="comunicati-archive-cta" data-testid="comunicati-archive-btn">
          Vedi tutti i comunicati <ArrowRight size={15} />
        </a>
      </FadeIn>
    </div>
  </section>
);

/* ========== ARCHIVIO COMUNICATI PAGE (SEO) ========== */
const ArchivioComunicatiPage = () => (
  <article className="comunicato-page" data-testid="archivio-comunicati-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back" data-testid="archivio-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <img src="/images/rai3-logo.jpg" alt="RAI" className="comunicato-rai-logo" />
        <div>
          <p className="comunicato-label">ARCHIVIO</p>
          <p className="comunicato-puntata">Tutti i comunicati stampa</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">Comunicati Stampa</h1>

      <div className="archivio-list">
        {comunicatiList.map((c) => (
          <a key={c.id} href={c.link} className="archivio-item" data-testid={`archivio-item-${c.id}`}>
            <div className="archivio-item-meta">
              <span className="archivio-item-date">{c.date}</span>
              <span className="archivio-item-puntata">{c.puntata}</span>
            </div>
            <h2 className="archivio-item-title">{c.title}</h2>
            <span className="archivio-item-link">
              Leggi il comunicato <ArrowRight size={14} />
            </span>
          </a>
        ))}
      </div>
    </div>
  </article>
);

/* ========== NEWS SECTION ========== */
const newsItems = [
  {
    id: 1,
    date: "27 Luglio 2026",
    title: "Cultura. Al via Segreti d'Autore, dopodomani a Valle-Sessa Cilento (SA) il Premio a Eva Crosetta",
    excerpt: "\"All about Eve\", tutto su Eva. Il titolo originale del famoso film premio Oscar nel 1951, che nella traduzione italiana diventava \"Eva contro Eva\", si presta benissimo per sintetizzare la serata inaugurale di \"Segreti d'Autore\", il Festival ideato da Ruggero Cappuccio e diretto da Nadia Baldi.",
    link: "/comunicato-segreti-autore-premio-eva-crosetta-29-luglio-2026",
  },
  {
    id: 2,
    date: "21 Luglio 2026",
    title: "Premio \"Segreti d'Autore\" 2026 - ANSA",
    excerpt: "Eva Crosetta e Paola Turci sono le vincitrici del premio \"Segreti d'Autore\" 2026. La popolare autrice televisiva, storica conduttrice della trasmissione \"Sulla via di Damasco\", sar\u00e0 premiata il 29 luglio alle 21 a Palazzo Coppola di Valle-Sessa Cilento.",
    link: "https://www.ansa.it/campania/notizie/2026/07/21/a-eva-crosetta-e-paola-turci-il-premio-segreti-dautore-2026_32a66009-a453-4be8-96a2-b4c006f0c95f.html",
  },
  {
    id: 3,
    date: "08 Ottobre 2024",
    title: "Nuovo libro pubblicato da Rizzoli",
    excerpt: "\"Che colpa ne ho se sono nato in Congo all'ombra di un mango?\" \u00e8 finalmente disponibile in tutte le librerie e online. Un viaggio tra le storie delle persone incontrate in Africa.",
    link: "https://www.amazon.it/colpa-sono-Congo-allombra-mango/dp/8817188743/",
  },
];

const NewsSection = () => (
  <section id="news" className="news-section" data-testid="news-section">
    <div className="news-inner">
      <FadeIn>
        <p className="news-overline">Aggiornamenti</p>
        <h2 className="news-title">News</h2>
      </FadeIn>
      {newsItems.map((item, i) => (
        <FadeIn key={item.id} delay={0.15 * (i + 1)}>
          <a href={item.link} target={item.link.startsWith("/") ? "_self" : "_blank"} rel="noopener noreferrer"
            className="news-card-compact" data-testid={`news-card-${item.id}`}>
            <span className="news-article-date">{item.date}</span>
            <h3 className="news-compact-title">{item.title}</h3>
            <p className="news-compact-excerpt">{item.excerpt}</p>
            <span className="news-compact-link">
              Leggi di pi&ugrave; <ArrowRight size={14} />
            </span>
          </a>
        </FadeIn>
      ))}
    </div>
  </section>
);

/* ========== RASSEGNA STAMPA ========== */
const rassegnaItems = [
  { name: "Roma", file: "/rassegna/rassegna-roma.pdf" },
  { name: "Corriere del Mezzogiorno", file: "/rassegna/rassegna-corriere-mezzogiorno.pdf" },
  { name: "Il Mattino - Salerno", file: "/rassegna/rassegna-mattino-salerno.pdf" },
  { name: "La Repubblica - Napoli", file: "/rassegna/rassegna-repubblica-napoli.pdf" },
  { name: "Il Mattino.it", file: "/rassegna/rassegna-mattino-it.pdf" },
];

const RassegnaStampaSection = () => (
  <section id="rassegna" className="rassegna-section" data-testid="rassegna-section">
    <div className="rassegna-inner">
      <FadeIn>
        <div className="rassegna-header">
          <img src="/images/segreti-autore-logo.webp" alt="Segreti d'Autore - Festival" className="rassegna-logo" />
          <div>
            <p className="rassegna-overline">Rassegna Stampa</p>
            <h2 className="rassegna-title">Premio Segreti d&apos;Autore 2026</h2>
          </div>
        </div>
      </FadeIn>
      <div className="rassegna-grid">
        {rassegnaItems.map((item, i) => (
          <FadeIn key={item.name} delay={0.1 * (i + 1)}>
            <a href={item.file} target="_blank" rel="noopener noreferrer"
              className="rassegna-card" data-testid={`rassegna-${i}`}>
              <Newspaper size={20} />
              <span>{item.name}</span>
              <ExternalLink size={14} />
            </a>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

/* ========== CONTACT ========== */
const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setError("Si \u00e8 verificato un errore. Riprova pi\u00f9 tardi.");
    }
    setLoading(false);
  };

  return (
    <section id="contact" className="contact-section" data-testid="contact-section">
      <div className="contact-inner">
        <FadeIn>
          <p className="contact-overline">Contatti</p>
          <h2 className="contact-title">Restiamo in contatto</h2>
          <a href="mailto:info@evacrosetta.com" className="contact-email-link" data-testid="contact-email-link">
            info@evacrosetta.com
          </a>
        </FadeIn>
        <FadeIn delay={0.2}>
          {success ? (
            <p className="form-success" data-testid="contact-form-success">
              Messaggio inviato con successo! Grazie per avermi contattato.
            </p>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit} data-testid="contact-form">
              <div className="form-group">
                <label htmlFor="name">Nome</label>
                <input id="name" type="text" placeholder="Il tuo nome" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-input-name" />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input id="email" type="email" placeholder="La tua email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-input-email" />
              </div>
              <div className="form-group">
                <label htmlFor="message">Messaggio</label>
                <textarea id="message" placeholder="Scrivi il tuo messaggio..." value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="contact-input-message" />
              </div>
              {error && <p style={{ color: "#B26B50", fontSize: "0.85rem" }} data-testid="contact-form-error">{error}</p>}
              <button type="submit" className="form-submit-btn" disabled={loading} data-testid="contact-submit-btn">
                {loading ? "Invio in corso..." : "Invia Messaggio"}
              </button>
            </form>
          )}
        </FadeIn>
      </div>
    </section>
  );
};

/* ========== FOOTER ========== */
const Footer = () => (
  <footer className="footer" data-testid="footer">
    <div className="footer-social">
      <a href="https://www.facebook.com/evacrosetta" target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook"><FaFacebookF /></a>
      <a href="https://www.instagram.com/evacrosetta" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram"><FaInstagram /></a>
      <a href="https://twitter.com/evacrosetta" target="_blank" rel="noopener noreferrer" data-testid="footer-twitter" aria-label="Twitter"><FaXTwitter /></a>
      <a href="https://www.youtube.com/@evacrosetta4312" target="_blank" rel="noopener noreferrer" data-testid="footer-youtube" aria-label="YouTube"><FaYoutube /></a>
    </div>
    <p className="footer-text">&copy; {new Date().getFullYear()} Eva Crosetta. Tutti i diritti riservati.</p>
  </footer>
);

/* ========== SHARE BUTTONS ========== */
const WhatsAppIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const ShareButtons = ({ title }) => {
  const url = `https://evacrosetta.com${window.location.pathname}`;
  return (
    <div className="share-buttons" data-testid="share-buttons">
      <p className="share-label">Condividi questo comunicato</p>
      <div className="share-row">
        <a
          href={`https://wa.me/?text=${encodeURIComponent(`${title} — ${url}`)}`}
          target="_blank" rel="noopener noreferrer"
          className="share-btn share-whatsapp" data-testid="share-whatsapp-btn"
        >
          <WhatsAppIcon /> WhatsApp
        </a>
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
          target="_blank" rel="noopener noreferrer"
          className="share-btn share-facebook" data-testid="share-facebook-btn"
        >
          <FacebookIcon /> Facebook
        </a>
      </div>
    </div>
  );
};

/* ========== COMUNICATO "CUSTODIRE L'AMORE" 6 NOVEMBRE PAGE (SEO) ========== */
const ComunicatoCustodireAmoreNovembrePage = () => (
  <article className="comunicato-page" data-testid="comunicato-custodireamore2-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back" data-testid="comunicato-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <img src="/images/rai3-logo.jpg" alt="RAI" className="comunicato-rai-logo" />
        <div>
          <p className="comunicato-label">COMUNICATO STAMPA</p>
          <p className="comunicato-puntata">TX 06.11.2026 &middot; ore 15.30</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">
        Sulla Via di Damasco, RAI Tre:<br />&ldquo;Custodire l&rsquo;amore&rdquo;
      </h1>

      <div className="comunicato-body">
        <p>
          <strong>ROMA</strong> &ndash; L&apos;amore &egrave; ancora una promessa per tutta la vita? In un tempo in cui le
          relazioni sembrano sempre pi&ugrave; fragili e la promessa del &ldquo;per sempre&rdquo; fa paura,
          <strong> Sulla Via di Damasco</strong>, nella collocazione speciale di domani (<strong>venerd&igrave; 6 novembre</strong>),
          <strong> ore 15.30, Rai Tre</strong>, si interroga sulla sfida pi&ugrave; grande: custodire l&apos;amore.
        </p>
        <p>
          Ospiti di questa puntata, <strong>Gigi De Palo</strong>, presidente della Fondazione per la Natalit&agrave;, e la
          moglie <strong>Anna Chiara Gambini</strong>, sposati da oltre vent&apos;anni e genitori di cinque figli. Sui social
          raccontano la loro vita di coppia e di fede senza filtri, con ironia e autenticit&agrave;. Con loro, il racconto di
          un amore che non &egrave; immune dalle fatiche, ma che si costruisce giorno dopo giorno, tra famiglia, lavoro,
          difficolt&agrave; e nuovi slanci.
        </p>
        <p>
          Le voci dei giovani raccolte nel primo servizio restituiscono dubbi e speranze di una generazione divisa tra il
          desiderio di amare per sempre e la paura di vedere infrante le proprie promesse. A pesare sono le difficolt&agrave;
          economiche, la routine, le responsabilit&agrave; e il timore di ripetere gli errori dei genitori.
        </p>
        <p>
          Spazio, infine, alla storia di <strong>Silvia e Giovanni</strong>: una separazione, il dolore e la scelta di provare
          a ricominciare grazie a <strong>Retrouvaille</strong>. Perch&eacute; il &ldquo;per sempre&rdquo; non &egrave; la
          promessa di una vita senza difficolt&agrave;, ma il coraggio di affrontarle insieme. &Egrave; questo il cuore del
          programma di <strong>Vito Sidoti, Eva Crosetta, Emiliano Fiore</strong>: un invito a non arrendersi e a credere
          nell&apos;amore.
        </p>
        <p>
          Regia di <strong>Alessandro Rosati</strong>.
        </p>
        <p className="comunicato-footer-date">
          Roma, 05.11.2026
        </p>
      </div>

      <ShareButtons title={'Sulla Via di Damasco, RAI Tre: "Custodire l\'amore" - venerdì 6 novembre ore 15.30'} />
    </div>
  </article>
);

/* ========== COMUNICATO "IL SANTO DEI GIOVANI" PAGE (SEO) ========== */
const ComunicatoSantoGiovaniPage = () => (
  <article className="comunicato-page" data-testid="comunicato-santogiovani-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back" data-testid="comunicato-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <img src="/images/rai3-logo.jpg" alt="RAI" className="comunicato-rai-logo" />
        <div>
          <p className="comunicato-label">COMUNICATO STAMPA</p>
          <p className="comunicato-puntata">Puntata n. 44 &middot; TX 01.11.2026</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">
        Sulla Via di Damasco, RAI Tre:<br />&ldquo;Il santo dei giovani&rdquo;
      </h1>

      <div className="comunicato-body">
        <p>
          <strong>ROMA</strong> &ndash; Sempre connessi, ma spesso soli. In un mondo dominato dagli smartphone e dalla ricerca
          di approvazione, la storia di <strong>Carlo Acutis</strong> lancia una sfida alle nuove generazioni: non vivere da
          fotocopie, ma fare della propria vita un capolavoro.
        </p>
        <p>
          Nella festa di Tutti i Santi, <strong>domenica 1 novembre, ore 7.30, Rai Tre</strong>, <strong>Eva Crosetta</strong> incontra
          <strong> don Maurizio Mirilli</strong>, direttore dell&apos;Ufficio per la Pastorale Universitaria della Diocesi di
          Roma, per scoprire il messaggio attualissimo di un ragazzo capace di vivere con gioia e autenticit&agrave;, anche
          davanti alla prova della malattia e alla prospettiva della morte.
        </p>
        <p>
          Attraverso le testimonianze della madre, <strong>Antonia Salzano</strong>, e di <strong>Rajesh Mohur</strong>,
          collaboratore della famiglia Acutis, riaffiorano i ricordi pi&ugrave; intimi di Carlo: la sua fede
          nell&apos;Eucaristia, la generosit&agrave; verso i poveri, la capacit&agrave; di accogliere ogni persona e di
          trasmettere, con la semplicit&agrave; dei suoi gesti, una speranza capace di cambiare la vita.
        </p>
        <p>
          Il programma di <strong>Vito Sidoti, Eva Crosetta, Emiliano Fiore</strong> propone un viaggio emozionante alla
          scoperta di una vita che continua a illuminare il presente e a ricordarci che la vera rivoluzione, anche
          nell&apos;era digitale, nasce dal cuore.
        </p>
        <p>
          Regia di <strong>Marina Gambini</strong>.
        </p>
        <p className="comunicato-footer-date">
          Roma, 30.10.2026
        </p>
      </div>

      <ShareButtons title={'Sulla Via di Damasco, RAI Tre: "Il santo dei giovani"'} />
    </div>
  </article>
);

/* ========== COMUNICATO "CUSTODIRE L'AMORE" PAGE (SEO) ========== */
const ComunicatoCustodireAmorePage = () => (
  <article className="comunicato-page" data-testid="comunicato-custodireamore-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back" data-testid="comunicato-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <img src="/images/rai3-logo.jpg" alt="RAI" className="comunicato-rai-logo" />
        <div>
          <p className="comunicato-label">COMUNICATO STAMPA</p>
          <p className="comunicato-puntata">Puntata n. 43 &middot; TX 25.10.2026</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">
        Sulla Via di Damasco, RAI Tre:<br />&ldquo;Custodire l&rsquo;amore&rdquo;
      </h1>

      <div className="comunicato-body">
        <p>
          <strong>ROMA</strong> &ndash; L&apos;amore &egrave; ancora una promessa per tutta la vita? In un tempo in cui le
          relazioni sembrano sempre pi&ugrave; fragili e la promessa del &ldquo;per sempre&rdquo; fa paura,
          <strong> Sulla Via di Damasco</strong>, <strong>domenica 25 ottobre, ore 7.30, Rai Tre</strong>, si interroga sulla
          sfida pi&ugrave; grande: custodire l&apos;amore.
        </p>
        <p>
          Ospiti di questa puntata, <strong>Gigi De Palo</strong>, presidente della Fondazione per la Natalit&agrave;, e la
          moglie <strong>Anna Chiara Gambini</strong>, sposati da oltre vent&apos;anni e genitori di cinque figli. Sui social
          raccontano la loro vita di coppia e di fede senza filtri, con ironia e autenticit&agrave;. Con loro, il racconto di
          un amore che non &egrave; immune dalle fatiche, ma che si costruisce giorno dopo giorno, tra famiglia, lavoro,
          difficolt&agrave; e nuovi slanci.
        </p>
        <p>
          Le voci dei giovani raccolte nel primo servizio restituiscono dubbi e speranze di una generazione divisa tra il
          desiderio di amare per sempre e la paura di vedere infrante le proprie promesse. A pesare sono le difficolt&agrave;
          economiche, la routine, le responsabilit&agrave; e il timore di ripetere gli errori dei genitori.
        </p>
        <p>
          Spazio, infine, alla storia di <strong>Silvia e Giovanni</strong>: una separazione, il dolore e la scelta di provare
          a ricominciare grazie a <strong>Retrouvaille</strong>. Perch&eacute; il &ldquo;per sempre&rdquo; non &egrave; la
          promessa di una vita senza difficolt&agrave;, ma il coraggio di affrontarle insieme. &Egrave; questo il cuore del
          programma di <strong>Vito Sidoti, Eva Crosetta, Emiliano Fiore</strong>: un invito a non arrendersi e a credere
          nell&apos;amore.
        </p>
        <p>
          Regia di <strong>Alessandro Rosati</strong>.
        </p>
        <p className="comunicato-footer-date">
          Roma, 23.10.2026
        </p>
      </div>

      <ShareButtons title={'Sulla Via di Damasco, RAI Tre: "Custodire l\'amore"'} />
    </div>
  </article>
);

/* ========== COMUNICATO "MAI PIU' SOLI" PAGE (SEO) ========== */
const ComunicatoMaiPiuSoliPage = () => (
  <article className="comunicato-page" data-testid="comunicato-maipiusoli-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back" data-testid="comunicato-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <img src="/images/rai3-logo.jpg" alt="RAI" className="comunicato-rai-logo" />
        <div>
          <p className="comunicato-label">COMUNICATO STAMPA</p>
          <p className="comunicato-puntata">Puntata n. 41 &middot; TX 11.10.2026</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">
        Sulla Via di Damasco, RAI Tre:<br />&ldquo;Mai pi&ugrave; soli&rdquo;
      </h1>

      <div className="comunicato-body">
        <p>
          <strong>ROMA</strong> &ndash; A <strong>&ldquo;Sulla Via di Damasco&rdquo;</strong> la storia di
          <strong> Elena Improta</strong> e della <strong>&ldquo;Casa di Mario&rdquo;</strong>: dalla solitudine alla scelta di
          costruire comunit&agrave;. Pu&ograve; accadere nella casa accanto alla nostra, nella disperazione che non ha voce o
          che non trova ascolto. &Egrave; un&apos;emergenza silenziosa: la solitudine. &Egrave; da qui che parte la nuova puntata
          di &ldquo;Sulla Via di Damasco&rdquo;, in onda <strong>domenica 11 ottobre, ore 7.30, su Rai 3</strong>, che accende
          i riflettori su una condizione che pu&ograve; attraversare ogni et&agrave;, ogni storia, ogni condizione.
        </p>
        <p>
          <strong>Eva Crosetta</strong> sar&agrave; a Orbetello (GR), nella Casa di Mario, per incontrare
          <strong> Elena Improta</strong>, madre e caregiver, presidente della Fondazione La Casa di Mario ETS. Al centro del
          racconto c&apos;&egrave; Mario, suo figlio, e una domanda che accompagna il futuro di tanti genitori di persone con
          disabilit&agrave;: <em>&ldquo;che cosa sar&agrave; di mio figlio quando io non ci sar&ograve; pi&ugrave;?&rdquo;</em>
        </p>
        <p>
          Un viaggio che da Orbetello arriva a Roma, dove un servizio racconta l&apos;esperienza della
          <strong> Comunit&agrave; di Sant&apos;Egidio</strong>: persone anziane che scelgono di vivere insieme, condividendo
          la casa, la quotidianit&agrave; e le relazioni.
        </p>
        <p>
          Due esperienze, una domanda urgente: come riconoscere la solitudine prima che sia troppo tardi? &Egrave; la domanda
          al centro del programma di <strong>Vito Sidoti, Eva Crosetta, Emiliano Fiore</strong>, che racconta un&apos;emergenza
          del nostro tempo attraverso storie vere, cercando nella realt&agrave; non solo il volto della fragilit&agrave;, ma
          anche le risposte.
        </p>
        <p>
          Regia di <strong>Alessandro Rosati</strong>.
        </p>
        <p className="comunicato-footer-date">
          Roma, 09.10.2026
        </p>
      </div>

      <ShareButtons title={'Sulla Via di Damasco, RAI Tre: "Mai più soli"'} />
    </div>
  </article>
);

/* ========== COMUNICATO "FRANCESCO, IL SANTO DELLA GIOIA" PAGE (SEO) ========== */
const ComunicatoSantoGioiaPage = () => (
  <article className="comunicato-page" data-testid="comunicato-santogioia-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back" data-testid="comunicato-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <img src="/images/rai3-logo.jpg" alt="RAI" className="comunicato-rai-logo" />
        <div>
          <p className="comunicato-label">COMUNICATO STAMPA</p>
          <p className="comunicato-puntata">Puntata n. 40 &middot; TX 04.10.2026</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">
        Sulla Via di Damasco, RAI Tre:<br />&ldquo;Francesco, il santo della gioia&rdquo;
      </h1>

      <div className="comunicato-body">
        <p>
          <strong>ROMA</strong> &ndash; Ci sono date che appartengono alla nostra storia e alla nostra identit&agrave;.
          Il 4 ottobre &egrave; una di queste: il giorno dedicato a <strong>San Francesco d&apos;Assisi</strong>, uno dei santi
          pi&ugrave; amati e conosciuti al mondo. Nel cuore del <strong>Giubileo Francescano</strong>, la nuova puntata di
          <strong> Sulla via di Damasco</strong>, in onda <strong>domenica 4 ottobre alle 7.30, Rai Tre</strong>,
          accompagner&agrave; i telespettatori alla scoperta di un Francesco vivo, concreto e sorprendentemente contemporaneo.
        </p>
        <p>
          <strong>Eva Crosetta</strong> incontra <strong>fra Marcello Longhi</strong>, direttore dell&apos;Opera San Francesco
          per i Poveri, per un intenso viaggio nel cuore del messaggio francescano. Un percorso che porta anche nelle periferie
          di Napoli, dove vecchi vagoni ferroviari sono diventati luoghi di accoglienza per i pi&ugrave; fragili, e incontra le
          storie di chi ha ritrovato la propria strada seguendo le orme di Francesco.
        </p>
        <p>
          A impreziosire il racconto, la testimonianza di <strong>Francesco Salvi</strong>, che porta sullo schermo
          <em> La leggenda dei tre compagni</em>, e quella di <strong>Giovanni</strong>, che dopo una dolorosa crisi personale
          ha trovato proprio ad Assisi la possibilit&agrave; di un nuovo inizio.
        </p>
        <p>
          Perch&eacute;, dopo ottocento anni, Francesco continua a parlarci? E quale sarebbe oggi il suo primo invito
          all&apos;umanit&agrave;? Il programma di <strong>Vito Sidoti, Eva Crosetta, Emiliano Fiore</strong>, tenter&agrave;
          di rispondere a queste domande riscoprendo l&apos;uomo oltre il santo, la forza rivoluzionaria della semplicit&agrave;
          e quella gioia capace, ancora oggi, di contagiare il mondo.
        </p>
        <p>
          Regia di <strong>Marina Gambini</strong>.
        </p>
        <p className="comunicato-footer-date">
          Roma, 02.10.2026
        </p>
      </div>

      <ShareButtons title={'Sulla Via di Damasco, RAI Tre: "Francesco, il santo della gioia"'} />
    </div>
  </article>
);

/* ========== COMUNICATO "SENZA PAURA" PAGE (SEO) ========== */
const ComunicatoSenzaPauraPage = () => (
  <article className="comunicato-page" data-testid="comunicato-senzapaura-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back" data-testid="comunicato-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <img src="/images/rai3-logo.jpg" alt="RAI" className="comunicato-rai-logo" />
        <div>
          <p className="comunicato-label">COMUNICATO STAMPA</p>
          <p className="comunicato-puntata">Puntata n. 39 &middot; TX 27.09.2026</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">
        Sulla Via di Damasco, RAI Tre:<br />&ldquo;Senza paura&rdquo;
      </h1>

      <div className="comunicato-body">
        <p>
          <strong>ROMA</strong> &ndash; Contro la &rsquo;ndrangheta, il coraggio di dire no. Quella di
          <strong> domenica 27 settembre, alle 7.30 su Rai Tre</strong>, sar&agrave; una puntata dedicata al coraggio di chi
          ha scelto di vivere fino in fondo i valori della giustizia e del Vangelo, senza piegarsi alla paura, anche quando
          difendere la verit&agrave; e la libert&agrave; pu&ograve; avere un prezzo altissimo.
        </p>
        <p>
          Nello studio di <strong>Eva Crosetta</strong>, <strong>Antonino De Masi</strong>, imprenditore calabrese da 13 anni
          sotto scorta, che ha sfidato la &rsquo;ndrangheta e pagato sulla propria pelle il prezzo della denuncia. La paura,
          la solitudine, gli anni sotto protezione. E una convinzione: la legalit&agrave; non &egrave; soltanto rispettare una
          legge, ma avere il coraggio di scegliere da che parte stare.
        </p>
        <p>
          Accanto alla sua storia, quella di <strong>Pasquale Sacc&agrave;</strong>, l&apos;imprenditore che ha detto no al
          pizzo e ha trovato nella fede la forza per resistere.
        </p>
        <p>
          E poi, la voce del Procuratore della Repubblica di Napoli, <strong>Nicola Gratteri</strong>: la battaglia contro le
          mafie non si combatte soltanto nelle Procure. Servono scuole, famiglie, comunit&agrave;. Servono sacerdoti,
          parrocchie e oratori capaci di costruire con i ragazzi relazioni, valori e futuro.
        </p>
        <p>
          Il programma di <strong>Vito Sidoti, Eva Crosetta, Emiliano Fiore</strong>, sottolinea una verit&agrave; semplice:
          contro la mafia, il coraggio di uno pu&ograve; diventare il coraggio di tanti.
        </p>
        <p>
          Regia di <strong>Alessandro Rosati</strong>.
        </p>
        <p className="comunicato-footer-date">
          Roma, 25.09.2026
        </p>
      </div>

      <ShareButtons title={'Sulla Via di Damasco, RAI Tre: "Senza paura"'} />
    </div>
  </article>
);

/* ========== COMUNICATO "MARIA PARLA OGGI" PAGE (SEO) ========== */
const ComunicatoMariaPage = () => (
  <article className="comunicato-page" data-testid="comunicato-maria-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back" data-testid="comunicato-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <img src="/images/rai3-logo.jpg" alt="RAI" className="comunicato-rai-logo" />
        <div>
          <p className="comunicato-label">COMUNICATO STAMPA</p>
          <p className="comunicato-puntata">Puntata n. 38 &middot; TX 20.09.2026</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">
        Sulla Via di Damasco, RAI Tre:<br />&ldquo;Maria parla oggi&rdquo;
      </h1>

      <div className="comunicato-body">
        <p>
          <strong>ROMA</strong> &ndash; Le apparizioni mariane, tra fede, mistero e speranza, al centro della prossima puntata di
          <strong> &ldquo;Sulla Via di Damasco&rdquo;</strong>, in onda <strong>domenica 20 settembre, su Rai Tre, ore 7.30</strong>.
          Maria parla ancora all&apos;uomo di oggi? Sono segni dal Cielo? Che cosa ci rivela?
        </p>
        <p>
          A pochi giorni dal pellegrinaggio di <strong>Papa Leone XIV</strong> a Lourdes, il programma condotto da
          <strong> Eva Crosetta</strong> affronta il tema delle apparizioni mariane con <strong>Saverio Gaeta</strong>,
          giornalista, scrittore e saggista, da anni studioso di Maria, dei fenomeni soprannaturali e delle apparizioni.
        </p>
        <p>
          Da <strong>Lourdes</strong> a <strong>Fatima</strong>, fino alle <strong>Tre Fontane</strong>, la puntata entra nel cuore
          di alcune delle pi&ugrave; potenti esperienze mariane del nostro tempo, l&agrave; dove fede, mistero e testimonianza si
          incontrano. Un viaggio alla ricerca dei segni attraverso cui la Chiesa riconosce l&apos;autenticit&agrave; di un&apos;apparizione.
        </p>
        <p>
          Tra le testimonianze, quella di <strong>suor Luigina Traverso</strong>, che racconta la guarigione vissuta a Lourdes,
          e le parole di <strong>padre Carlos Cabecinhas</strong>, rettore del Santuario di Fatima. Spazio anche alla vicenda di
          <strong> Bruno Cornacchiola</strong>, protagonista dell&apos;apparizione delle Tre Fontane, attraverso una preziosa
          testimonianza d&apos;archivio.
        </p>
        <p>
          Il programma di <strong>Vito Sidoti, Eva Crosetta, Emiliano Fiore</strong> propone un viaggio tra storia, testimonianze
          e spiritualit&agrave; per arrivare alla domanda centrale: che cosa cerca oggi l&apos;uomo nei santuari mariani?
        </p>
        <p>
          Regia di <strong>Marina Gambini</strong>.
        </p>
        <p className="comunicato-footer-date">
          Roma, 16.09.2026
        </p>
      </div>

      <ShareButtons title={'Sulla Via di Damasco, RAI Tre: "Maria parla oggi"'} />
    </div>
  </article>
);

/* ========== COMUNICATO "LA FORZA DELLA GRATITUDINE" PAGE (SEO) ========== */
const ComunicatoGratitudinePage = () => (
  <article className="comunicato-page" data-testid="comunicato-gratitudine-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back" data-testid="comunicato-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <img src="/images/rai3-logo.jpg" alt="RAI" className="comunicato-rai-logo" />
        <div>
          <p className="comunicato-label">COMUNICATO STAMPA</p>
          <p className="comunicato-puntata">Puntata n. 32 &middot; TX 09.08.2026</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">
        Sulla Via di Damasco, RAI Tre:<br />&ldquo;La forza della gratitudine&rdquo;
      </h1>

      <div className="comunicato-body">
        <p>
          <strong>ROMA</strong> &ndash; La forza della gratitudine: il viaggio di <strong>Sulla Via di Damasco</strong> tra dolore,
          speranza e rinascita. Pu&ograve; la gratitudine cambiare il modo di guardare la vita, anche quando tutto sembra perduto?
          &Egrave; la domanda al centro della nuova puntata del programma di <strong>Rai Cultura</strong>, condotto da
          <strong> Eva Crosetta</strong>, in onda domenica su <strong>Rai 3</strong>, il prossimo <strong>9 agosto, ore 8.30</strong>.
        </p>
        <p>
          Nel cuore del Casentino (AR), nella <strong>Pieve di Romena</strong>, dove ha sede l&apos;omonima Fraternit&agrave;,
          Eva Crosetta incontra <strong>don Gigi Verdi</strong>, che da oltre trent&apos;anni accoglie chi cerca ascolto, pace e
          un nuovo inizio. Un dialogo intenso sul dolore, sulla fragilit&agrave;, sui giovani e sulla forza della gratitudine
          come scelta capace di aprire orizzonti di speranza.
        </p>
        <p>
          Ad arricchire la puntata, tre storie di coraggio e riscatto: la scuola di Caivano guidata dalla preside
          <strong> Eugenia Carfora</strong>, l&apos;esperienza di <strong>GOEL</strong> con <strong>Vincenzo Linarello</strong>,
          che sfida la &apos;ndrangheta costruendo sviluppo e legalit&agrave;, e il racconto della giornalista
          <strong> Barbara Schiavulli</strong>, testimone di chi, anche nei territori di guerra, continua a scegliere la pace.
        </p>
        <p>
          Il programma di <strong>Vito Sidoti, Eva Crosetta, Emiliano Fiore</strong> torna a riflettere su come il bene,
          la fiducia e la gratitudine possano ancora generare cambiamento.
        </p>
        <p>
          Regia di <strong>Alessandro Rosati</strong>.
        </p>
        <p className="comunicato-footer-date">
          Roma, 07.08.2026
        </p>
      </div>

      <ShareButtons title={'Sulla Via di Damasco, RAI Tre: "La forza della gratitudine"'} />
    </div>
  </article>
);

/* ========== COMUNICATO SEGRETI D'AUTORE PAGE (SEO) ========== */
const ComunicatoSegretiPage = () => (
  <article className="comunicato-page" data-testid="comunicato-segreti-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back" data-testid="comunicato-segreti-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <div>
          <p className="comunicato-label">COMUNICATO STAMPA</p>
          <p className="comunicato-puntata">Segreti d&apos;Autore 2026 &middot; 29 Luglio 2026</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">
        Al via Segreti d&apos;Autore, il 29 luglio a Valle-Sessa Cilento (SA) il Premio a Eva Crosetta
      </h1>
      <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", fontWeight: 400, fontStyle: "italic", color: "var(--text-secondary)", marginBottom: "2.5rem" }}>
        &ldquo;All about Eve&rdquo;, tutto su Eva.
      </h2>

      <div className="comunicato-body">
        <p>
          Come nel capolavoro cinematografico del 1951 <em>&ldquo;Eva contro Eva&rdquo;</em>, la protagonista dell&apos;evento inaugurale
          di <strong>Segreti d&apos;Autore</strong>, il Festival ideato da <strong>Ruggero Cappuccio</strong> e diretto da <strong>Nadia Baldi</strong>,
          che quest&apos;anno affronta i temi di Ambiente, Scienze, Arti e Legalit&agrave;, &egrave; una donna che ha saputo far convergere
          sulla propria persona molte attenzioni.
        </p>
        <p>
          <strong>Eva Crosetta</strong>, giornalista e autrice televisiva, storica conduttrice Rai di <em>&ldquo;Sulla via di Damasco&rdquo;</em>,
          ricever&agrave; <strong>mercoled&igrave; 29 luglio, alle ore 21</strong>, a <strong>Palazzo Coppola di Valle-Sessa Cilento (Sa)</strong>,
          il premio <strong>&ldquo;Segreti d&apos;Autore 2026&rdquo;</strong>, una preziosa opera d&apos;arte del Maestro <strong>Mimmo Paladino</strong>.
        </p>
        <p>
          Nel corso della serata presenter&agrave; il suo libro <strong>&ldquo;Che colpa ne ho se sono nato in Congo all&apos;ombra
          di un mango?&rdquo;</strong>, pubblicato da <strong>Rizzoli</strong>. Il racconto sincero e ricco di umanit&agrave; del suo
          percorso di conversione e fede in seguito all&apos;incontro con <strong>don Matteo Galloni</strong> e <strong>Francesca
          Termanini</strong> della <strong>Comunit&agrave; Amore e Libert&agrave;</strong> che dal 1988 accoglie bambini e ragazzi soli,
          in Italia e nell&apos;estrema periferia della capitale congolese.
        </p>
        <p>
          A Palazzo Coppola inoltre sar&agrave; possibile visitare, dalle 18 alle 20, due mostre: <strong>&ldquo;La stanza di
          Herlitzka&rdquo;</strong>, dedicata al grande attore scomparso nell&apos;estate di due anni fa, con l&apos;esposizione
          curata dalla regista <strong>Nadia Baldi</strong>, e la mostra di <strong>Antonella Romano</strong>, <em>&ldquo;Quando
          il bambino era bambino&rdquo;</em>.
        </p>

        <div style={{ marginTop: "2rem", padding: "1.5rem", background: "var(--bg-alt)", borderRadius: "8px", borderLeft: "3px solid var(--accent-primary)" }}>
          <p style={{ marginBottom: "0.75rem" }}><strong>Informazioni per l&apos;ingresso:</strong></p>
          <p>
            &Egrave; necessaria la prenotazione inviando una mail a: <a href="mailto:comunicazione@festivalsegretidautore.it" style={{ color: "var(--accent-primary)" }}>comunicazione@festivalsegretidautore.it</a>
          </p>
          <p>
            Specificare nell&apos;email: evento, numero dei posti, nome e cognome. All&apos;ingresso mostrare la ricevuta di conferma
            (cartacea o su dispositivo elettronico).
          </p>
        </div>

        <p className="comunicato-footer-date">
          Il Festival Segreti d&apos;Autore ha il sostegno del Ministero della Cultura e della Regione Campania con i fondi del Pnrr.
        </p>
      </div>

      <ShareButtons title={'Premio Segreti d\'Autore 2026 a Eva Crosetta'} />
    </div>
  </article>
);

/* ========== COMUNICATO "NEL NOME DI FRANCESCO" PAGE (SEO) ========== */
const ComunicatoFrancescoPage = () => (
  <article className="comunicato-page" data-testid="comunicato-francesco-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <img src="/images/rai3-logo.jpg" alt="RAI" className="comunicato-rai-logo" />
        <div>
          <p className="comunicato-label">COMUNICATO STAMPA</p>
          <p className="comunicato-puntata">Puntata n. 31 &middot; TX 02.08.2026</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">
        Sulla Via di Damasco, RAI Tre:<br />&ldquo;Nel nome di Francesco&rdquo;
      </h1>

      <div className="comunicato-body">
        <p>
          <strong>ASSISI</strong> &ndash; <strong>Sulla Via di Damasco</strong> torna alle radici della novit&agrave; francescana.
          C&apos;&egrave; un uomo senza il quale oggi forse non conosceremmo San Francesco, cos&igrave; come lo conosciamo.
          &Egrave; <strong>Tommaso da Celano</strong>, il primo biografo del Poverello.
        </p>
        <p>
          Sulle tracce di questo frate schivo, ma testimone prezioso, prende il via la nuova puntata di <strong>Sulla Via di Damasco</strong>,
          in onda <strong>domenica 02 agosto, ore 8.30, su Rai Tre</strong>, un viaggio nel cuore di <strong>Assisi</strong>,
          nell&apos;anno giubilare dedicato al Santo, a ottocento anni dalla sua morte.
        </p>
        <p>
          A guidare il racconto sar&agrave; <strong>Mons. Felice Accrocca</strong>, Arcivescovo di Assisi e tra i pi&ugrave; autorevoli
          studiosi del francescanesimo medievale, che accompagner&agrave; <strong>Eva Crosetta</strong> alla scoperta del volto
          pi&ugrave; autentico di Francesco. Un dialogo che attraversa i grandi temi del perdono, della pace e della fraternit&agrave;,
          oggi pi&ugrave; che mai urgenti.
        </p>
        <p>
          La puntata racconta Francesco attraverso la figura di Tommaso da Celano, protagonista del film <strong>&ldquo;Parola di
          Tommaso&rdquo;</strong> di <strong>Matteo Vanni</strong>, e ricostruisce l&apos;affascinante vicenda delle prime biografie
          francescane tra antichi manoscritti, testi ritrovati e una memoria custodita nei secoli che continua ancora oggi a parlare al mondo.
        </p>
        <p>
          Tra storia, spiritualit&agrave; e cinema, il programma di <strong>Vito Sidoti, Eva Crosetta, Emiliano Fiore</strong> restituisce
          il volto pi&ugrave; autentico del Santo di Assisi e il valore di una testimonianza che, dopo ottocento anni, continua a
          interrogare il presente.
        </p>
        <p>
          Regia di <strong>Alessandro Rosati</strong>.
        </p>
        <p className="comunicato-footer-date">
          Roma, 31.07.2026
        </p>
      </div>

      <ShareButtons title={'Sulla Via di Damasco, RAI Tre: "Nel nome di Francesco"'} />
    </div>
  </article>
);

/* ========== NEWS "TRA LA PIAZZA E IL CIELO" PAGE (SEO) ========== */
const NewsFestivalPage = () => (
  <article className="comunicato-page" data-testid="news-festival-page">
    <div className="comunicato-page-inner">
      <a href="/" className="comunicato-back" data-testid="news-festival-back">
        <ChevronLeft size={16} /> Torna alla Home
      </a>

      <div className="comunicato-header">
        <div>
          <p className="comunicato-label">NEWS</p>
          <p className="comunicato-puntata">Campania Libri Festival &middot; 1&ndash;4 Ottobre 2026, Napoli</p>
        </div>
      </div>

      <h1 className="comunicato-page-title">
        Tra la piazza e il cielo: Eva Crosetta al Campania Libri Festival con un percorso dedicato alla spiritualit&agrave;
      </h1>

      <img
        src="/images/campania-libri-festival.webp"
        alt="Campania Libri Festival 2026 - Jesce Sole - Palazzo Reale e Biblioteca Nazionale, Napoli"
        className="news-festival-image"
      />

      <div className="comunicato-body">
        <p>
          Dal <strong>1&deg; al 4 ottobre 2026</strong>, Napoli torna a essere luogo d&apos;incontro tra libri, idee e persone con il
          <strong> Campania Libri Festival</strong>, ospitato negli spazi di <strong>Palazzo Reale</strong> e della
          <strong> Biblioteca Nazionale</strong>.
        </p>
        <p>
          All&apos;interno del Festival nasce <strong>&ldquo;Tra la piazza e il cielo&rdquo;</strong>, un percorso dedicato alla
          spiritualit&agrave; a cura di <strong>Eva Crosetta</strong>, da un&apos;idea di <strong>Ruggero Cappuccio</strong>.
        </p>
        <p>
          Un titolo che racchiude gi&agrave; il senso degli incontri: mettere in dialogo ci&ograve; che accade ogni giorno sotto
          i nostri occhi con ci&ograve; che continua a interrogarci nel profondo.
        </p>

        <h2 className="comunicato-body-subtitle">La spiritualit&agrave; come modo di abitare il mondo</h2>
        <p>
          &ldquo;Tra la piazza e il cielo&rdquo; parte da una domanda contemporanea: che spazio ha oggi la spiritualit&agrave;
          nella vita dell&apos;uomo?
        </p>
        <p>
          Non una spiritualit&agrave; intesa come fuga dalla realt&agrave;, ma come un modo diverso di attraversarla e comprenderla.
          Le relazioni, il dolore, la scienza, i conflitti, le fragilit&agrave; e la ricerca di senso diventano cos&igrave; territori
          nei quali continuare a interrogarsi.
        </p>
        <p>
          Eva Crosetta accompagner&agrave; il pubblico attraverso tre incontri e tre prospettive differenti, mettendo al centro il
          dialogo con personalit&agrave; provenienti da esperienze e mondi diversi: <strong>Padre Giulio Albanese</strong>,
          <strong> Cristian Brogna</strong> e <strong>Franco Vaccari</strong>.
        </p>

        <h2 className="comunicato-body-subtitle">1 ottobre &ndash; Padre Giulio Albanese</h2>
        <p>
          Il primo appuntamento &egrave; con <strong>Padre Giulio Albanese</strong>, missionario e giornalista, per uno sguardo
          sulla spiritualit&agrave; che incontra la storia e le sue ferite.
        </p>
        <p>
          Guerre, povert&agrave;, periferie del mondo e responsabilit&agrave; verso gli altri diventano il punto di partenza per
          riflettere su una fede che non rimane distante dalla realt&agrave;, ma entra nelle contraddizioni del nostro tempo.
        </p>
        <p className="news-festival-event">
          <strong>1 ottobre 2026 &ndash; ore 12:30</strong><br />
          Palazzo Reale &ndash; Sala De Li Cunti<br />
          Piazza del Plebiscito 1, Napoli
        </p>

        <h2 className="comunicato-body-subtitle">2 ottobre &ndash; Cristian Brogna</h2>
        <p>
          Il secondo incontro porta il percorso sul confine affascinante tra scienza, cervello e mistero della vita.
        </p>
        <p>
          Protagonista sar&agrave; <strong>Cristian Brogna</strong>, neurochirurgo, per interrogarsi sulle straordinarie
          potenzialit&agrave; del cervello umano e su quel territorio nel quale la conoscenza scientifica riesce a illuminare molto,
          lasciando per&ograve; ancora aperte domande profonde sull&apos;essere umano.
        </p>
        <p className="news-festival-event">
          <strong>2 ottobre 2026 &ndash; ore 11:15</strong><br />
          Palazzo Reale &ndash; Sala De Li Cunti<br />
          Piazza del Plebiscito 1, Napoli
        </p>

        <h2 className="comunicato-body-subtitle">3 ottobre &ndash; Franco Vaccari</h2>
        <p>
          Il terzo appuntamento vedr&agrave; protagonista <strong>Franco Vaccari</strong>, fondatore di
          <strong> Rondine Cittadella della Pace</strong>.
        </p>
        <p>
          La spiritualit&agrave; diventa qui soprattutto relazione: ascolto, comprensione, incontro e riconoscimento dell&apos;altro
          come persona.
        </p>
        <p>
          Un dialogo che parte dall&apos;esperienza di Rondine e dalla possibilit&agrave; di affrontare conflitti, paure e
          fragilit&agrave; attraverso una cultura capace di rimettere al centro la persona.
        </p>
        <p className="news-festival-event">
          <strong>3 ottobre 2026 &ndash; ore 12:30</strong><br />
          Palazzo Reale &ndash; Sala De Li Cunti<br />
          Piazza del Plebiscito 1, Napoli
        </p>

        <h2 className="comunicato-body-subtitle">Tre incontri, una stessa domanda</h2>
        <p>
          Tre strade apparentemente lontane &ndash; la fede vissuta nelle periferie del mondo, la scienza che indaga il cervello
          umano e il dialogo come risposta al conflitto &ndash; finiscono per convergere verso la stessa domanda:
          <strong> come possiamo continuare a cercare un senso senza smettere di stare pienamente nel mondo?</strong>
        </p>
        <p>
          &Egrave; proprio nello spazio simbolico tra la piazza e il cielo, tra la concretezza della vita quotidiana e il bisogno
          umano di guardare oltre, che il percorso curato da Eva Crosetta invita il pubblico a fermarsi, ascoltare e interrogarsi.
        </p>
        <p>
          Perch&eacute; forse &egrave; proprio l&igrave; che continua a giocarsi la nostra idea di essere umani.
        </p>
      </div>

      <ShareButtons title={'Tra la piazza e il cielo: Eva Crosetta al Campania Libri Festival'} />
    </div>
  </article>
);

/* ========== APP ========== */
/* ========== HOME PAGE ========== */
const HomePage = () => (
  <>
    <FestivalPopup />
    <Navigation />
    <HeroSection />
    <BookSection />
    <BiographySection />
    <TVShowSection />
    <ComunicatiStampaSection />
    <NewsSection />
    <RassegnaStampaSection />
    <ContactSection />
    <Footer />
  </>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/comunicato-stampa-sulla-via-di-damasco-nel-nome-di-francesco" element={<ComunicatoFrancescoPage />} />
        <Route path="/comunicato-segreti-autore-premio-eva-crosetta-29-luglio-2026" element={<ComunicatoSegretiPage />} />
        <Route path="/comunicato-stampa-sulla-via-di-damasco-la-forza-della-gratitudine" element={<ComunicatoGratitudinePage />} />
        <Route path="/comunicato-stampa-sulla-via-di-damasco-maria-parla-oggi" element={<ComunicatoMariaPage />} />
        <Route path="/comunicato-stampa-sulla-via-di-damasco-senza-paura" element={<ComunicatoSenzaPauraPage />} />
        <Route path="/comunicato-stampa-sulla-via-di-damasco-francesco-il-santo-della-gioia" element={<ComunicatoSantoGioiaPage />} />
        <Route path="/comunicato-stampa-sulla-via-di-damasco-mai-piu-soli" element={<ComunicatoMaiPiuSoliPage />} />
        <Route path="/comunicato-stampa-sulla-via-di-damasco-custodire-l-amore" element={<ComunicatoCustodireAmorePage />} />
        <Route path="/comunicato-stampa-sulla-via-di-damasco-il-santo-dei-giovani" element={<ComunicatoSantoGiovaniPage />} />
        <Route path="/comunicato-stampa-sulla-via-di-damasco-custodire-l-amore-6-novembre" element={<ComunicatoCustodireAmoreNovembrePage />} />
        <Route path="/comunicati-stampa" element={<ArchivioComunicatiPage />} />
        <Route path="/news-tra-la-piazza-e-il-cielo-campania-libri-festival" element={<NewsFestivalPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
